import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerProfileModal from './CustomerProfileModal';
import { finesService, errorMessage } from '../services/finesService';
import Button from '../ui/Button';
import IconButton from '../ui/IconButton';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import { matchNumber } from './quickActionMatch';

// Click a contract number or a Customer ID ANYWHERE in the app (a table cell, a chip, a
// heading ...) and a small card offers the next step: open in Ejar, search in The
// Engine, Customer profile. One listener on the document, so every page gets it without
// being touched. Inputs are left alone; the card closes on outside click / Escape.
// What counts as a contract number / Customer ID: quickActionMatch.js (a date, ticket or booking
// number is no longer taken for a Customer ID - CON-14).
const EJAR_WEB = 'https://ejarprod.iyelo.com:6300';

// The pointer must be ON the number itself (the text node under the cursor), not just
// inside a box that happens to contain it - clicking empty space in a card or a modal
// header must do nothing.
const detect = (el, x, y) => {
    let textNode = null;
    try {
        if (document.caretPositionFromPoint) { const p = document.caretPositionFromPoint(x, y); textNode = p && p.offsetNode; }
        else if (document.caretRangeFromPoint) { const r = document.caretRangeFromPoint(x, y); textNode = r && r.startContainer; }
    } catch (err) { textNode = null; }
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return null;
    const hit = matchNumber(textNode.textContent, { identityTag: !!(textNode.parentElement && textNode.parentElement.closest('[data-quick-kind="identity"]')) });
    if (!hit) return null;
    // and the click really sits on that text (not on padding beside it)
    const range = document.createRange();
    range.selectNodeContents(textNode);
    const box = range.getBoundingClientRect();
    if (x < box.left - 4 || x > box.right + 4 || y < box.top - 4 || y > box.bottom + 4) return null;
    return hit;
};

export default function ContractQuickActions() {
    const navigate = useNavigate();
    const [pop, setPop] = useState(null);   // { kind, value, x, y }
    const [profileSeed, setProfileSeed] = useState(null);
    const [msg, setMsg] = useState(null);   // { text, error }
    const openRef = useRef(false);          // the listeners below read whether the card is open
    useEffect(() => { openRef.current = !!pop; }, [pop]);

    useEffect(() => {
        // Capture phase: a number rendered as a link / button on some pages has its own
        // click handler (copy + open the Ejar list); the card takes over everywhere.
        const onClick = (e) => {
            const t = e.target;
            if (!(t instanceof Element)) return;
            if (t.closest('#contract-quick-actions')) return;
            // inside a dialog only the customer profile offers the menu (its contract numbers)
            const inProfile = !!t.closest('#customer-profile-modal');
            // ... and a dialog that asks for it (data-quick-actions="on": the Overview's lists - owner, 3 Oct 2026: "لما
            // بدوس على رقم العقد القائمة مش بتظهر")
            const inDialog = inProfile || !!t.closest('[data-quick-actions="on"]');
            // any other click in a dialog is the dialog's own (its Close, its footer link): the card goes away with it
            if (t.closest('[role="dialog"]') && !inDialog) { setPop(null); return; }
            if (t.closest('input, textarea, select, [contenteditable="true"]')) { setPop(null); return; }
            // Numbers rendered by the shared table cell (CellId) carry their value, so a click
            // anywhere on them works; other text falls back to the precise text-node check.
            const tagged = t.closest('[data-quick-value]');
            const hit = (tagged && matchNumber(tagged.getAttribute('data-quick-value'), { identityTag: tagged.getAttribute('data-quick-kind') === 'identity' }))
                || detect(t, e.clientX, e.clientY);
            if (!hit) { setPop(null); return; }
            e.preventDefault();
            e.stopPropagation();
            const x = Math.min(e.clientX, window.innerWidth - 300);
            const y = Math.min(e.clientY + 8, window.innerHeight - 190);
            setMsg(null);
            setPop({ ...hit, x, y, inProfile, inDialog });
        };
        // Escape closes the card first: window's capture phase comes before a dialog's own Escape (the document's capture
        // phase, which stops the key) - otherwise the dialog closed and the card stayed on the page under it
        const onKey = (e) => {
            if (e.key !== 'Escape' || !openRef.current) return;
            e.stopPropagation();
            setPop(null);
        };
        document.addEventListener('click', onClick, true);
        window.addEventListener('keydown', onKey, true);
        return () => { document.removeEventListener('click', onClick, true); window.removeEventListener('keydown', onKey, true); };
    }, []);

    const goEngine = (q) => {
        setPop(null);
        setProfileSeed(null);
        const url = `/the-engine?tab=customer&q=${encodeURIComponent(q)}`;
        // already on The Engine: the page reads its query only on mount, so reload it there
        if (window.location.pathname === '/the-engine') window.location.assign(url); else navigate(url);
    };
    const openEjarCustomer = async (identity) => {
        setMsg({ text: 'Looking up the customer in Ejar…', error: false });
        try {
            const r = await finesService.ejarCustomerId({ identity });
            setPop(null);
            window.open(`${EJAR_WEB}/app/crm/customers/view/${r.customerId}`, '_blank', 'noopener');
        } catch (e) { setMsg({ text: errorMessage(e), error: true }); }
    };
    const copy = async (v) => { try { await navigator.clipboard.writeText(v); } catch (err) { /* clipboard blocked */ } setPop(null); };

    const popStyle = {
        position: 'fixed',
        left: pop ? pop.x : 0,
        top: pop ? pop.y : 0,
        zIndex: pop && pop.inDialog ? 'calc(var(--z-dialog) + 10)' : 'var(--z-dropdown)',   // above the dialog it was opened in
        background: 'var(--surface)',
        border: '2px solid var(--accent)',
        borderRadius: 'var(--radius-lg)',
        transition: 'border-color var(--duration-fast) var(--ease)',
        boxShadow: 'var(--shadow-lg)',
    };
    const item = 'justify-start';
    return (
        <>
            {pop && (
                <div id="contract-quick-actions" role="group" aria-label={`Actions for ${pop.value}`}
                    style={popStyle}
                    className="w-[290px] max-w-[calc(100vw-16px)] p-3 flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <span className="font-mono font-semibold text-gray-900">{pop.value}</span>
                        <IconButton icon={ICONS.close} label="Close" size="sm" onClick={() => setPop(null)} />
                    </div>
                    {pop.kind === 'contract' ? (
                        <>
                            <a href={`${EJAR_WEB}/app/rental/contracts/viewBynumber/${encodeURIComponent(pop.value)}`} target="_blank" rel="noreferrer"
                                onClick={() => setPop(null)} className={`yelo-btn yelo-btn--secondary yelo-btn--sm yelo-btn--full ${item}`}>
                                <span className="yelo-btn__icon"><Icon glyph={ICONS.arrowRight} size="sm" /></span>
                                <span className="yelo-btn__label">Open in Ejar</span>
                            </a>
                            <Button variant="primary" size="sm" fullWidth className={item} leadingIcon={<Icon glyph={ICONS.engine} size="sm" />} onClick={() => goEngine(pop.value)}>Search in The Engine</Button>
                            {!pop.inProfile && <Button size="sm" fullWidth className={item} leadingIcon={<Icon glyph={ICONS.users} size="sm" />} onClick={() => { setProfileSeed({ contractNo: pop.value }); setPop(null); }}>Customer profile</Button>}
                        </>
                    ) : (
                        <>
                            {!pop.inProfile && <Button variant="primary" size="sm" fullWidth className={item} leadingIcon={<Icon glyph={ICONS.users} size="sm" />} onClick={() => { setProfileSeed({ identity: pop.value }); setPop(null); }}>Customer profile</Button>}
                            <Button size="sm" fullWidth className={item} leadingIcon={<Icon glyph={ICONS.arrowRight} size="sm" />} onClick={() => openEjarCustomer(pop.value)}>Open customer in Ejar</Button>
                            <Button size="sm" fullWidth className={item} leadingIcon={<Icon glyph={ICONS.engine} size="sm" />} onClick={() => goEngine(pop.value)}>Search in The Engine</Button>
                        </>
                    )}
                    {msg && (
                        <div className={`flex items-start gap-1.5 text-xs ${msg.error ? 'text-red-700' : 'text-gray-600'}`} role={msg.error ? 'alert' : 'status'}>
                            {msg.error && <Icon glyph={ICONS.error} size="xs" className="mt-px" />}
                            <span>{msg.text}</span>
                        </div>
                    )}
                    <Button variant="ghost" size="xs" className="self-start" leadingIcon={<Icon glyph={ICONS.copy} size="xs" />} onClick={() => copy(pop.value)}>Copy the number</Button>
                </div>
            )}
            <CustomerProfileModal open={!!profileSeed} seed={profileSeed} onClose={() => setProfileSeed(null)} onCopy={() => {}} />
        </>
    );
}
